import { useState, useEffect } from 'react';

export default function CookieConsentSection() {
  const [data, setData] = useState({
    desktop_text: '',
    mobile_text: '',
    accept_button_text: '',
    reject_button_text: '',
    privacy_policy_url: '',
    privacy_policy_label: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/cookie_consent.php');
      const result = await response.json();
      if (result) {
        setData({
          desktop_text: result.desktop_text || '',
          mobile_text: result.mobile_text || '',
          accept_button_text: result.accept_button_text || 'Aceitar Cookies',
          reject_button_text: result.reject_button_text || 'Rejeitar',
          privacy_policy_url: result.privacy_policy_url || '',
          privacy_policy_label: result.privacy_policy_label || 'Política de Privacidade'
        });
      }
    } catch (err) {
      setMessage('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/cookie_consent.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      setMessage(result.success ? 'Salvo com sucesso!' : 'Erro ao salvar');
    } catch (err) {
      setMessage('Erro ao salvar dados');
    } finally {
      setIsSaving(false);
    }
  };

  const renderPreview = (text: string) => {
    if (!text) return 'Sem texto definido';
    return text.replace(
      '{privacy_link}',
      `[${data.privacy_policy_label || 'Política de Privacidade'}]`
    );
  };

  if (isLoading) return <div className="p-4 text-gray-500">Carregando...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Barra de Consentimento de Cookies</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          🍪 Configure o texto e botões da barra de cookies que aparece na parte inferior do site quando o utilizador visita pela primeira vez.
        </p>
        <p className="text-xs text-blue-600 mt-2">
          💡 Use <code className="bg-blue-100 px-1 py-0.5 rounded text-blue-800 font-mono">{'{privacy_link}'}</code> no texto para inserir automaticamente o link da Política de Privacidade.
        </p>
      </div>

      <div className="space-y-4">
        {/* Desktop Text */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Texto - Desktop</h3>
          <textarea
            value={data.desktop_text}
            onChange={(e) => setData({ ...data, desktop_text: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-vertical"
            rows={3}
            placeholder="Este website utiliza cookies para melhorar a sua experiência de navegação. Ao continuar a navegar, concorda com a nossa utilização de cookies. Consulte a nossa {privacy_link} para mais informações."
          />
          <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-500 mb-1 font-semibold">Pré-visualização:</p>
            <p className="text-sm text-gray-700">{renderPreview(data.desktop_text)}</p>
          </div>
        </div>

        {/* Mobile Text */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Texto - Mobile</h3>
          <textarea
            value={data.mobile_text}
            onChange={(e) => setData({ ...data, mobile_text: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-vertical"
            rows={3}
            placeholder="Este website utiliza cookies para melhorar a sua experiência. Consulte a nossa {privacy_link}."
          />
          <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-500 mb-1 font-semibold">Pré-visualização:</p>
            <p className="text-sm text-gray-700">{renderPreview(data.mobile_text)}</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Botões</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Texto do botão "Aceitar"</label>
              <input
                type="text"
                value={data.accept_button_text}
                onChange={(e) => setData({ ...data, accept_button_text: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Aceitar Cookies"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Texto do botão "Rejeitar"</label>
              <input
                type="text"
                value={data.reject_button_text}
                onChange={(e) => setData({ ...data, reject_button_text: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Rejeitar"
              />
            </div>
          </div>
        </div>

        {/* Privacy Policy Link */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Link da Política de Privacidade</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">URL</label>
              <input
                type="url"
                value={data.privacy_policy_url}
                onChange={(e) => setData({ ...data, privacy_policy_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="https://financor.pt/politica-de-privacidade/"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Texto do link</label>
              <input
                type="text"
                value={data.privacy_policy_label}
                onChange={(e) => setData({ ...data, privacy_policy_label: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Política de Privacidade"
              />
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 mb-3">Pré-visualização da Barra</h3>
          <div
            className="rounded-lg overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #3a1a0a 0%, #562310 50%, #6B3410 100%)',
              borderTop: '3px solid #8B4513'
            }}
          >
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-2xl flex-shrink-0">🍪</span>
                <p className="text-white text-sm" style={{ lineHeight: '1.5' }}>
                  {data.desktop_text
                    ? data.desktop_text.split('{privacy_link}').map((part, i, arr) => (
                        <span key={i}>
                          {part}
                          {i < arr.length - 1 && (
                            <span
                              style={{
                                color: '#f0c070',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px'
                              }}
                            >
                              {data.privacy_policy_label || 'Política de Privacidade'}
                            </span>
                          )}
                        </span>
                      ))
                    : 'Sem texto definido'}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <span
                  className="px-4 py-2 rounded-lg text-xs font-medium"
                  style={{
                    color: '#d4a574',
                    border: '1.5px solid #d4a574'
                  }}
                >
                  {data.reject_button_text || 'Rejeitar'}
                </span>
                <span
                  className="px-4 py-2 rounded-lg text-xs font-semibold"
                  style={{
                    color: '#3a1a0a',
                    background: 'linear-gradient(135deg, #f0c070 0%, #e0a850 100%)'
                  }}
                >
                  {data.accept_button_text || 'Aceitar Cookies'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.includes('sucesso') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
      </button>
    </div>
  );
}
